import { yupResolver } from '@hookform/resolvers/yup';
import {
  getAgentsKey,
  useSuspendAgent,
  AgentModel,
  AgentMutationInput,
} from 'api-hooks/agent';
import { CircleSVG, CloseSVG } from 'common/assets';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { UserStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
  Title,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import BillingAddressCard from './billing-address-card';
import { AgentFormSchema, AgentFormType } from './form-type';
import ContactInformationCard from './pic-information-card';
import PurchaseSystemCard from './purchase-system-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  agent?: AgentModel;
  onSubmit: (
    input: AgentMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<AgentModel | undefined>;
}

export default function AgentForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { agent } = props;
  const dialog = useDialog();
  const { can } = useAuthorization();
  const { close, navigate } = useNavigation();
  const { mutateAsync: suspendMutate, isLoading: suspendLoading } =
    useSuspendAgent();

  const defaultValues = useGetDefaultValues(agent);

  const methods = useForm<AgentFormType>({
    resolver: yupResolver(AgentFormSchema()),
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const onSuspend = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('common:confirmation_suspend_text'),
      title: t('common:confirmation'),
      onPositiveAction: async (dismiss) => {
        try {
          dismiss();

          await suspendMutate({
            agentId: agent!.id!,
            body: { action: UserStatusEnum.UnderSurveillance },
          });
          queryClient.refetchQueries([getAgentsKey()[0]]);

          close();

          notification.success({ message: t('common:successfully_suspended') });
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (dismiss) => {
        dismiss();
      },
    });
  }, [agent, close, suspendMutate, dialog, t]);

  const onUnsuspend = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('common:confirmation_unsuspend_text'),
      title: t('common:confirmation'),
      onPositiveAction: async (dismiss) => {
        try {
          dismiss();
          await suspendMutate({
            agentId: agent!.id!,
            body: { action: UserStatusEnum.Safe },
          });
          queryClient.refetchQueries([getAgentsKey()[0]]);

          close();

          notification.success({
            message: t('common:successfully_unsuspended'),
          });
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
    });
  }, [agent, close, dialog, suspendMutate, t]);

  const onViewMutation = () => {
    navigate(NavigationRoutes.AgentMutationReport, {
      filter: {
        agent: agent!.id!,
      },
    });
  };

  const isSafe = agent?.status === UserStatusEnum.Safe;
  const permission = useGetCrudPermission('agents');
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!agent}
      customViewName={agent && [t('common:agent'), agent.name].join(' - ')}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={agent}
            title={
              agent ? (
                <>
                  <FullContainer
                    $style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Title variant="HeadingXSmall">
                      {t('common:agent')} - {agent.name}
                    </Title>
                    {agent.status !== UserStatusEnum.Safe && (
                      <>
                        <Separator gap={16} />
                        <CircleSVG />
                      </>
                    )}
                  </FullContainer>
                </>
              ) : (
                t('common:add_extra', { extra: t('common:agent') })
              )
            }
            noDelete={
              editable || !can(AuthorizationRules.AgentsUpdateSurveillance)
            }
            onDelete={isSafe ? onSuspend : onUnsuspend}
            noEdit={!permission.update}
            deleteText={
              isSafe ? t('customer:suspend') : t('customer:unsuspend')
            }
            isDeleteLoading={suspendLoading}
            deleteIcon={
              <CloseSVG
                color={stylesColors.redDefault}
                width={16}
                height={16}
              />
            }
          />
        )}
      </FormState>
      <div>
        <CardSection>
          <Text variant="HeadingXSmall">{t('agent:agent_detail')}</Text>
          <SeparatorVertical $margin={16} />
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="name"
                label={t('common:name')}
                placeholder={t('common:name')}
                required
                fontCase="TitleCase"
              />
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <OneThirdContainer>
              <Input
                type="email"
                name="email"
                label={t('common:email')}
                placeholder={t('common:email')}
              />
            </OneThirdContainer>
            <OneThirdContainer>
              <Input
                type="tel"
                name="phone"
                label={t('common:phone_number')}
                placeholder={t('common:phone_number')}
              />
            </OneThirdContainer>
            <OneThirdContainer>
              <Input
                type="tel"
                name="phone_2"
                label={t('cruise:cellphone_number')}
                placeholder={t('cruise:cellphone_number')}
              />
            </OneThirdContainer>
          </SectionContainer>
          <FullContainer>
            <Input
              type="text"
              name="address"
              label={t('common:address')}
              placeholder={t('common:address')}
              noMargin
              fontCase="TitleCase"
            />
          </FullContainer>
        </CardSection>
        <SeparatorVertical $margin={24} />
        <BillingAddressCard />
        <SeparatorVertical $margin={24} />
        <ContactInformationCard />
        <SeparatorVertical $margin={24} />
        {agent && (
          <>
            <CardSection>
              <SectionContainer>
                <Text variant="HeadingXSmall">{t('agent:agent_balance')}</Text>
                <Button buttonType="tertiary" onClick={onViewMutation}>
                  {t('common:view_mutation')}
                </Button>
              </SectionContainer>
              <SeparatorVertical $margin={16} />
              <FullContainer>
                <Input
                  type="number"
                  name="balance"
                  label={t('agent:balance')}
                  placeholder={t('agent:agent_balance')}
                  disabled={!!agent}
                  noMargin
                />
              </FullContainer>
            </CardSection>
            <SeparatorVertical $margin={24} />
          </>
        )}
        <PurchaseSystemCard />
        <SeparatorVertical $margin={32} />
      </div>
    </Form>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
  alignItems: 'center',
});
